import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTransaction } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Pattern C entity has no status column — derive a display status from the
// approval fields (mirrors the list page).
function deriveStatus(tx: Row): string {
  const reviewStatus = str(tx.review_status);
  if (reviewStatus) return reviewStatus.toLowerCase();
  return tx.is_approved ? "approved" : "pending";
}

export default function V2TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useTransaction(id ?? "");
  const transaction = (data as { transaction?: Row } | undefined)?.transaction;

  const displayId = str(transaction?.display_id) ?? "Transaction";
  const type = str(transaction?.transaction_type);
  const amount = num(transaction?.amount);
  const txDate = str(transaction?.transaction_date);
  const status = transaction ? deriveStatus(transaction) : null;

  const subtitleParts = [
    type ? titleCase(type) : null,
    txDate ? formatDate(txDate) : null,
  ].filter(Boolean) as string[];

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !transaction}
      title={displayId}
      subtitle={subtitleParts.join(" · ") || undefined}
      breadcrumbs={[
        { label: "Transactions", href: "/transactions" },
        { label: displayId },
      ]}
      notFoundMessage={{
        title: "Transaction not found",
        description: "This transaction may have been removed from the ledger.",
      }}
      actions={<DetailPageActions entityLabel="Transaction" />}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          <KPIRow
            cards={[
              {
                label: "Amount",
                value: amount != null ? formatCurrency(amount) : "—",
              },
              {
                label: "Status",
                value: status ? titleCase(status) : "—",
              },
              { label: "Type", value: type ? titleCase(type) : "—" },
              { label: "Date", value: txDate ? formatDate(txDate) : "—" },
            ]}
          />

          {str(transaction?.notes) && (
            <RailSection title="Notes">
              <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                {transaction?.notes as string}
              </p>
            </RailSection>
          )}
        </div>

        {/* Right rail — Pattern C: Properties + Linked records, no activity feed */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Status">
            {status ? <StatusBadge status={status} /> : <span>—</span>}
          </RailSection>

          <RailSection title="Properties">
            <InfoRow label="Type" value={type ? titleCase(type) : null} />
            <InfoRow
              label="Payment Type"
              value={
                str(transaction?.payment_type)
                  ? titleCase(transaction!.payment_type as string)
                  : null
              }
            />
            <InfoRow label="Category" value={str(transaction?.category)} />
            <InfoRow
              label="Expense Type"
              value={
                str(transaction?.expense_type)
                  ? titleCase(transaction!.expense_type as string)
                  : null
              }
            />
            <InfoRow
              label="Amount"
              value={amount != null ? formatCurrency(amount) : null}
            />
            <InfoRow
              label="Sales Tax"
              value={
                num(transaction?.sales_tax) != null
                  ? formatCurrency(transaction!.sales_tax as number)
                  : null
              }
            />
            <InfoRow
              label="Card Fee"
              value={
                num(transaction?.credit_card_fee) != null
                  ? formatCurrency(transaction!.credit_card_fee as number)
                  : null
              }
            />
            <InfoRow
              label="Approved"
              value={transaction?.is_approved ? "Yes" : "No"}
            />
            <InfoRow
              label="Created"
              value={
                transaction?.created_at
                  ? formatDate(transaction.created_at as string)
                  : null
              }
            />
          </RailSection>

          <RailSection title="Vendor">
            <InfoRow label="Name" value={str(transaction?.vendor_name)} />
            <InfoRow
              label="Vendor Invoice"
              value={str(transaction?.vendor_invoice)}
            />
            <InfoRow
              label="Check #"
              value={
                num(transaction?.check_number) != null
                  ? String(transaction!.check_number)
                  : null
              }
            />
          </RailSection>

          <RailSection title="Linked Records">
            <InfoRow
              label="Invoice"
              value={transaction?.invoice_id ? "Linked" : null}
            />
            <InfoRow
              label="Deal"
              value={transaction?.opportunity_id ? "Linked" : null}
            />
            <InfoRow
              label="Job"
              value={transaction?.job_id ? "Linked" : null}
            />
            <InfoRow
              label="Crew"
              value={transaction?.crew_id ? "Linked" : null}
            />
            <InfoRow
              label="Territory"
              value={transaction?.territory_id ? "Linked" : null}
            />
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
